import { createRef, forwardRef, HTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";
import { useAttendeeStatus } from "amazon-chime-sdk-component-library-react";
import { useActiveSpeakers } from "@platform/features/video/hooks/useActiveSpeakers";
import { RoomMember } from "@platform/features/video/types/video.types";
import VideoUITileNameplate from "../VideoUITileNameplate/VideoUITileNameplate";
import VideoUITileNameplateType from "../VideoUITileNameplate/VideoUITileNameplateType";
import VideoUITileAudioIcon from "./VideoUITileAudioIcon";
import useContainerWidthBreakpoint from "@core/hooks/useContainerWidthBreakpoint";

type ObjectFit = "contain" | "cover" | "fill" | "none" | "scale-down";

export interface VideoTileProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "css"> {
  id?: string;
  isEnabled?: boolean;
  isContentShare?: boolean;
  isWithContentShare?: boolean;
  tileIndex?: number;
  totalTiles?: number;
  nameplate?: string;
  objectFit?: ObjectFit;
  attendeeId?: string; // Add attendeeId prop for active speaker detection
  roomMemberData?: RoomMember | null;
  videoClassName?: string;
}

export const VideoUITile = forwardRef<HTMLVideoElement, VideoTileProps>(
  (
    {
      className,
      nameplate,
      isEnabled,
      isContentShare,
      isWithContentShare,
      tileIndex,
      totalTiles,
      objectFit = "cover",
      attendeeId,
      roomMemberData,
      videoClassName,
      ...rest
    },
    ref
  ) => {
    const containerRef = createRef<HTMLDivElement>();

    const { isXs, containerWidth } = useContainerWidthBreakpoint(containerRef);

    const { muted } = useAttendeeStatus(attendeeId || "");

    const { isActiveSpeaker } = useActiveSpeakers();

    const isSpeaking = attendeeId ? isActiveSpeaker(attendeeId) : false;

    return (
      <div
        className={twMerge(
          "relative rounded-md bg-brand-800 w-full h-full group transition-all duration-200",
          isSpeaking && "ring-1 ring-brand-100 ring-offset-1",
          className
        )}
        data-testid="video-tile"
        ref={containerRef}
        {...rest}
      >
        {isEnabled || isContentShare ? (
          <>
            <video
              ref={ref}
              className={twMerge(
                "absolute top-0 left-0 w-full h-full rounded-md group",
                objectFit === "cover" && "object-cover",
                objectFit === "contain" && "object-contain",
                objectFit === "fill" && "object-fill",
                objectFit === "none" && "object-none",
                objectFit === "scale-down" && "object-scale-down",
                videoClassName
              )}
            />
            {nameplate && (
              <VideoUITileNameplate
                nameplate={nameplate}
                isWithContentShare={!!isWithContentShare}
                isWithVideo={!!isEnabled}
                roomMemberData={roomMemberData}
                containerWidth={containerWidth}
                isXs={isXs}
              />
            )}
            <VideoUITileAudioIcon
              muted={muted}
              isWithContentShare={isWithContentShare}
            />
          </>
        ) : (
          <>
            <div
              className={twMerge(
                "w-full h-full flex items-center justify-center"
              )}
            >
              <VideoUITileNameplateType
                nameplate={nameplate || ""}
                isWithContentShare={!!isWithContentShare}
                isWithVideo={false}
                roomMemberData={roomMemberData}
                containerWidth={containerWidth}
                isXs={isXs}
              />
              <VideoUITileAudioIcon
                muted={muted}
                isWithContentShare={isWithContentShare}
              />
            </div>
          </>
        )}
      </div>
    );
  }
);

export default VideoUITile;
